/**
 * 튜닝형 문항 화면 — **003(SQL 개선)과 004(인덱스 설계)를 한 컴포넌트가 그린다.**
 *
 * 두 유형은 보여 주는 것과 보내는 칸만 다르고 흐름이 같다: 문제를 읽고 → 답을 써서 채점하고
 * → 점수와 수치를 보고 → 납득이 안 되면 재심을 요청한다. 그래서 베끼지 않고 `mode` 로 가른다
 * (이 저장소에서 두 벌로 나뉜 화면은 언제나 한쪽만 고쳐졌다).
 *
 * ## 화면이 반드시 보여 주는 것
 *
 *  · **수치 세 개** — 개선 전(출제자가 잰 값) · 모범답안 · 내 제출. 점수만 주면 왜 그런지 모른다.
 *  · **재심으로 점수가 바뀌었다는 사실** — 조용히 바꾸지 않는다. 기계 점수와 함께 보여 준다.
 *  · **중지 버튼** — 느린 SQL 을 실제로 끝까지 돌리므로 빠져나올 길이 있어야 한다.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiFileText, FiInfo, FiPlay, FiSquare, FiCpu, FiAlertTriangle, FiRefreshCw, FiActivity, FiAward } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getQuizConfig } from '../../config/quizTypes';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import {
  gradeTuning, cancelTuning, requestReview, reviewAvailable, tuningConfigOf, newGradeId, checkTuningReadiness, fetchTuningPlan,
} from '../../services/tuningGrade';
import { SERVER_GRADED, tuningGradingTarget } from '../../services/challengeDb';
import TuningRubricDialog from './TuningRubricDialog';

import { toCssPx } from '../../utils/appZoom';
const REDUCED_MOTION = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** 점수별 색 — 테마 변수만 쓴다(다크모드에서 깨지지 않게). */
function scoreStyle(score) {
  if (score >= 10) return { bg: 'var(--color-quiz-success-bg)', border: 'var(--color-quiz-success-border)', text: 'var(--color-quiz-success-text)' };
  if (score > 0) return { bg: 'var(--color-quiz-warn-bg)', border: 'var(--color-quiz-warn-border)', text: 'var(--color-quiz-warn-text)' };
  return { bg: 'var(--color-quiz-fail-bg)', border: 'var(--color-quiz-fail-border)', text: 'var(--color-quiz-fail-text)' };
}

/**
 * 지문 렌더링. 두 가지를 고친다:
 *  · 코드 블록에 테두리 — 개선 전 SQL 이 본문과 섞이지 않게
 *  · 문단 여백을 좁게 — 기본 1em 이라 지문 세 문단이면 점수·수치가 화면 밖으로 밀린다
 */
const tight = { margin: '4px 0' };
const mdComponents = {
  p: ({ node, ...props }) => <p {...props} style={tight} />,
  ul: ({ node, ordered, ...props }) => <ul {...props} style={{ ...tight, paddingLeft: 18 }} />,
  ol: ({ node, ordered, ...props }) => <ol {...props} style={{ ...tight, paddingLeft: 18 }} />,
  li: ({ node, ordered, ...props }) => <li {...props} style={{ margin: '2px 0' }} />,
  pre: ({ node, ...props }) => (
    <pre
      {...props}
      style={{
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 4,
        padding: 8,
        margin: '6px 0',
        overflow: 'auto',
        fontSize: 11,
        lineHeight: 1.5,
      }}
    />
  ),
  // 과제 범위 줄(> **과제 범위 …**)이 그냥 들여쓰기로만 보이지 않게 — 왼쪽 띠
  blockquote: ({ node, ...props }) => (
    <blockquote {...props} style={{ margin: '4px 0 6px', padding: '4px 8px', borderLeft: '3px solid var(--color-accent)', background: 'var(--color-bg)' }} />
  ),
  // react-markdown 새 판은 `inline` 을 넘기지 않는다 — 언어 표시도 줄바꿈도 없으면 문장 속 코드다(SingleChoiceQuiz 와 같은 판정)
  code: ({ node, inline, ...props }) => (
    <code
      {...props}
      style={(inline ?? (!/language-/.test(props.className || '') && !String(props.children).includes('\n')))
        ? { background: 'var(--color-bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 11 }
        : { fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}
    />
  ),
};

/**
 * 지난 제출 — 제출한 SQL 과 그때 점수를 꺼낸다.
 *
 * 점수는 저장할 때 `fail_rsn_cn` 에 `{"tuning":{...}}` 로 넣어 둔 것이다(로컬 저장소에 점수
 * 컬럼이 없어서 그렇게 했다). 깨져 있어도 화면이 죽지 않게 감싼다.
 */
function lastSubmissionOf(participation) {
  const sql = participation?.SBMSN_SQL_CN || '';
  if (!sql) return null;
  let score = null;
  try {
    const parsed = JSON.parse(participation?.FAIL_RSN_CN || '{}');
    if (parsed && typeof parsed.tuning?.score === 'number') score = parsed.tuning.score;
  } catch (_e) { /* 옛 기록이거나 다른 글이 들어 있을 수 있다 — 점수만 못 보여 줄 뿐이다 */ }
  return { sql, score, at: participation?.SBMSN_DT || null };
}

/** 같은 답인지 견주는 모양 — 공백·줄바꿈·끝 세미콜론·대소문자 차이는 같은 답으로 본다. */
const sameAnswerKey = (sql) => String(sql || '').replace(/;\s*$/, '').replace(/\s+/g, ' ').trim().toUpperCase();

/**
 * 「쿼리 수정 + 인덱스 변경」 답을 한 칸(제출 이력)에 담는 모양. 표식은 SQL 주석이라 그대로 읽어도 뜻이 통한다.
 * 표식이 없는 옛 제출은 CREATE INDEX 로 시작하면 인덱스, 아니면 SQL 로 읽는다.
 */
const SQL_MARK = '-- [SQL]';
const INDEX_MARK = '-- [INDEX]';
export function joinAnswer(sql, ddl) {
  const s = String(sql || '').trim();
  const d = String(ddl || '').trim();
  if (!s && !d) return '';
  return [s ? `${SQL_MARK}\n${s}` : '', d ? `${INDEX_MARK}\n${d}` : ''].filter(Boolean).join('\n');
}
export function splitAnswer(text) {
  const raw = String(text || '');
  const si = raw.indexOf(SQL_MARK);
  const ii = raw.indexOf(INDEX_MARK);
  if (si < 0 && ii < 0) {
    return /^\s*CREATE\s+(UNIQUE\s+)?INDEX\b/i.test(raw) ? { sql: '', ddl: raw.trim() } : { sql: raw.trim(), ddl: '' };
  }
  const part = (from, to) => (from < 0 ? '' : raw.slice(from, to < 0 || to < from ? undefined : to));
  const sqlText = part(si, ii).replace(SQL_MARK, '').trim();
  const ddlText = part(ii, si > ii ? si : -1).replace(INDEX_MARK, '').trim();
  return { sql: sqlText, ddl: ddlText };
}

/** 숫자를 읽기 좋게. 없으면 '—'. */
const n = (v) => (v == null ? '—' : Number(v).toLocaleString());

/**
 * 채점 사유를 **화면에서** 만든다.
 *
 * 서버는 `outcome`(코드)과 `detail`(수치)만 준다. 서버가 문장을 만들어 보내면 영어 모드에서도
 * 한국어가 그대로 나온다 — 실제로 그렇게 나갔다(지적 2026-09-19).
 */
function reasonOf(t, result) {
  const o = result?.outcome;
  // `detail` 이 없으면 **이미 가진 수치로 메운다.** 서버가 옛 판이면 detail 을 안 보내는데,
  // 그때 문장이 「논리 읽기 — (모범답안 —의 —배)」 로 비어 나왔다(지적 2026-09-19).
  // metrics·ratio 는 예나 지금이나 오므로 그것으로 채운다.
  const d = {
    submitted: result?.metrics?.submitted?.buffers ?? null,
    answer: result?.metrics?.answer?.buffers ?? null,
    ratio: result?.ratio ?? null,
    ...(result?.detail || {}),
  };
  if (d.ratio == null && d.submitted != null && d.answer) d.ratio = d.submitted / d.answer;
  if (!o) return result?.reason || '';
  // 평가 기준(루브릭) 결과 — 어느 종류의 어느 기준에 걸렸고, 감점이 왜 붙었는지
  if (d.category && d.rule) {
    const main = t(`challenge:tuning.rubric.rule.${d.rule}`, {
      kind: t(`challenge:tuning.rubric.kind.${d.category}`),
      final: n(d.final), baseline: n(d.baseline), model: n(d.model),
      full: n(d.limits?.full), partial: n(d.limits?.partial), base: d.base,
    });
    // 인덱스를 여럿 냈으면 인덱스마다(그 인덱스를 뺀 상태와 견줌), 하나면 1차 ↔ 2차로 말한다
    const perIndex = Array.isArray(d.penalties) && d.penalties.some(x => Array.isArray(x.columns));
    const pen = perIndex
      ? ' ' + d.penalties.map(x => t(`challenge:tuning.rubric.penaltyIndex.${x.rule}`, {
        columns: x.columns.join(', '), without: n(x.without), with: n(x.with), penalty: x.penalty,
      })).join(' ')
      : d.penaltyRule
        ? ' ' + t(`challenge:tuning.rubric.penalty.${d.penaltyRule}`, { p1: n(d.phase1), p2: n(d.phase2), penalty: d.penalty })
        : '';
    return main + pen;
  }
  const key = `challenge:tuning.reason.${o}`;
  return t(key, {
    submitted: n(d.submitted), answer: n(d.answer),
    ratio: d.ratio != null ? Number(d.ratio).toFixed(2) : '—',
    limit: d.fullLimit != null ? String(d.fullLimit) : '—',
    table: d.table || '', message: d.message || '',
  });
}

/** 서버가 준 주의 — `{code, params}` 를 번역한다. 옛 모양(문자열)도 받아 준다. */
function noteText(t, note) {
  if (typeof note === 'string') return note;
  if (!note || !note.code) return '';
  if (note.code === 'raw') return note.params?.text || '';
  return t(`challenge:tuning.note.${note.code}`, note.params || {});
}

const answerBox = {
  flex: 1, resize: 'none', padding: 8, boxSizing: 'border-box',
  background: 'var(--color-bg)', color: 'var(--color-text)',
  border: '1px solid var(--color-border)', borderRadius: 4,
  fontSize: 12, fontFamily: 'var(--font-mono)',
};

const box = {
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  padding: 8,
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
};

const TuningQuiz = ({
  quiz, challengeId, participation,
  // 채점할 연결은 prop 이 아니라 services/challengeDb 가 **누르는 순간** 정한다 — 웹은 서버의 교육DB,
  // 스탠드얼론은 활성 연결(리덕스 밖 모듈 변수라 값으로 받으면 나중에 연결한 사람에게 영영 「연결을 고르세요」가 뜬다)
  onGraded,           // (result) => Promise  — 제출 이력 저장은 부모가 한다
  onReset,            // () => Promise — 내 제출 기록을 지운다(초기화). 저장처럼 부모가 한다. 실패하면 던진다
  onShowPlans,        // (d) => void — 채점 계획을 왼쪽 실행계획 탭으로(모범답안 ↔ 내 제출 비교)
  isSaving,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const code = quiz?.QUIZ_TCD || '003';
  const isIndex = code === '004';
  const cfg = getQuizConfig(code) || {};
  const tuning = tuningConfigOf(quiz);
  // 과제 범위 — 평가 기준 ①(2026-10-05). 문항이 말하지 않으면 유형으로: 003 쿼리 수정만 · 004 인덱스 변경까지
  const scope = tuning.scope || (isIndex ? 'sql+index' : 'sql');
  // 대상 DBMS — 채점 DB 를 고르는 근거(서버 challengeDb). 예전 문항은 전부 Oracle 이다
  const dbms = tuning.dbms || 'oracle';
  const withIndex = scope === 'sql+index';

  // 답은 「수정 SQL」 과 「인덱스 DDL」 두 칸이다(인덱스 변경까지인 과제). 저장·견주기는 한 줄(joinAnswer)로 한다
  const [sqlPart, setSqlPart] = useState('');
  const [ddlPart, setDdlPart] = useState('');
  const answer = withIndex ? joinAnswer(sqlPart, ddlPart) : sqlPart;
  const setAnswer = useCallback((text) => {
    if (withIndex) {
      const p = splitAnswer(text);
      setSqlPart(p.sql); setDdlPart(p.ddl);
    } else {
      setSqlPart(text || '');
    }
  }, [withIndex]);
  const [rubricOpen, setRubricOpen] = useState(false);
  const [grading, setGrading] = useState(false);
  const [gradeId, setGradeId] = useState(null);
  const [result, setResult] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  // 방금 채점한 답 — 제출 이력이 다시 불려 오기 전에도 같은 답을 막으려고(이력 저장이 실패해도)
  const [gradedKey, setGradedKey] = useState(null);
  const [error, setError] = useState('');

  // 문항이 바뀌면 전부 비운다 — 앞 문항의 점수가 남아 있으면 자기 답의 결과로 오해한다
  useEffect(() => {
    setSqlPart(''); setDdlPart(''); setResult(null); setReview(null); setError(''); setGradeId(null); setGradedKey(null);
  }, [challengeId, quiz?.QUIZ_NO]);

  // 사전 확인 — 004 는 실습용 복제 표(PR_*)에 인덱스를 만든다. 표가 없으면 **문제를 고를 때** 알린다.
  // 제출하고 나서야 「표가 없다」 를 들으면 제출 횟수만 깎인다(지적 2026-10-04). 판정은 채점과 같은 서버 함수다.
  // null = 확인 전 / 확인 불가(막지 않는다), { ready:false, reason } = 제출을 막고 이유를 말한다
  const [readiness, setReadiness] = useState(null);
  const [checking, setChecking] = useState(false);
  const targetTable = isIndex ? tuning.targetTable : null;
  // 웹은 **교육DB 에 붙는지**까지 본다(대상 테이블이 없는 003 도) — 학생에게 연결을 요구하지 않는 대신
  // 채점 DB 가 내려가 있으면 제출 전에 알아야 한다. 스탠드얼론은 활성 연결의 테이블만 본다
  const runReadiness = useCallback(async () => {
    if (!targetTable && !SERVER_GRADED) { setReadiness(null); return; }
    const target = tuningGradingTarget();
    if (target.error) { setReadiness({ ready: false, reason: 'no_connection' }); return; }
    setChecking(true);
    try {
      setReadiness(await checkTuningReadiness({ connectionId: target.connectionId, targetTable, dbms }));
    } catch (_e) {
      setReadiness(null);   // 확인 자체가 실패하면 막지 않는다 — 채점이 구체적으로 말해 준다
    } finally {
      setChecking(false);
    }
  }, [targetTable, dbms]);
  useEffect(() => { runReadiness(); }, [challengeId, quiz?.QUIZ_NO]); // eslint-disable-line react-hooks/exhaustive-deps
  const blocked = !!(readiness && readiness.ready === false);

  // 제출 버튼이 바닥에 고정돼 결과는 스크롤 안에 나온다 — 채점이 끝나면 결과(또는 오류)를 보이게 끌어온다
  // **scrollIntoView 를 쓰지 말 것** — 스크롤할 수 있는 조상을 **전부** 움직여 앱의 작업 영역(overflow:hidden)까지
  // 밀려 올라가고, 편집기 툴바 줄이 화면 위로 사라졌다(지적 2026-10-05). 이 화면의 스크롤 칸만 움직인다.
  const resultRef = useRef(null);
  const scrollRef = useRef(null);
  useEffect(() => {
    const box = scrollRef.current;
    const anchor = resultRef.current;
    if ((result || error) && box && anchor) {
      // rect 차이는 화면 px — scrollTop 은 CSS px(웹의 화면 확대, utils/appZoom)
      const top = toCssPx(anchor.getBoundingClientRect().top - box.getBoundingClientRect().top) + box.scrollTop;
      box.scrollTo({ top, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    }
  }, [result, error]);

  const last = lastSubmissionOf(participation);
  const submissionCount = participation?.SBMSN_NOT ?? 0;
  const maxSubmissions = cfg.maxSubmissions || 5;
  const remaining = Math.max(0, maxSubmissions - submissionCount);
  // **이미 채점한 답을 다시 내지 못하게 한다**(지적 2026-10-05: 재심 결과를 보는데 제출 버튼이 켜져 있었다).
  // 같은 답을 다시 내면 점수는 같고 제출 횟수만 준다. 고치면 다시 켜진다. 재심 중에도 막는다(결과가 바뀌는 중이다)
  const unchanged = !!answer.trim()
    && (sameAnswerKey(answer) === gradedKey || (!!last && sameAnswerKey(answer) === sameAnswerKey(last.sql)));
  const canGrade = remaining > 0 && !!answer.trim() && !grading && !blocked && !unchanged && !reviewing;

  const handleGrade = useCallback(async () => {
    const target = tuningGradingTarget();
    if (target.error) {
      setError(t('challenge:tuning.needConnection'));
      return;
    }
    const { connectionId } = target;   // 웹은 비어 있다 — 서버가 교육DB 로 잰다
    const id = newGradeId();
    setGradeId(id); setGrading(true); setError(''); setResult(null); setReview(null);
    try {
      // 평가 기준(루브릭) 채점 — 원래 쿼리를 알아야 한다(1차·2차를 그것과 견준다). 모르는 옛 문항만 예전 방식
      const originalSql = isIndex ? tuning.probeSql : tuning.slowSql;
      const payload = originalSql
        ? {
          gradeId: id, connectionId, scope, originalSql, dbms,
          answerSql: quiz?.CRANS_SQL_CN || (isIndex ? tuning.probeSql : ''),
          // 모범 인덱스 — 여럿일 수 있다(OR 문항). 없으면 answerColumns 하나
          answerIndexes: isIndex
            ? (Array.isArray(tuning.answerIndexes) && tuning.answerIndexes.length
              ? tuning.answerIndexes.map(cols => ({ columns: cols, unique: false }))
              : (Array.isArray(tuning.answerColumns) && tuning.answerColumns.length
                ? [{ columns: tuning.answerColumns, unique: !!tuning.answerUnique }] : []))
            : [],
          submittedSql: sqlPart.trim(),
          submittedDdl: withIndex ? ddlPart.trim() : '',
          targetTable: tuning.targetTable || null,
          thresholds: tuning.thresholds || null,
        }
        : isIndex
        ? {
          gradeId: id, connectionId, kind: 'index',
          probeSql: tuning.probeSql, targetTable: tuning.targetTable,
          answerColumns: tuning.answerColumns, answerUnique: tuning.answerUnique,
          submittedDdl: answer,
          tolerance: tuning.tolerance, partialFactor: tuning.partialFactor,
        }
        : {
          gradeId: id, connectionId, kind: 'sql',
          answerSql: quiz?.CRANS_SQL_CN, submittedSql: answer,
          tolerance: tuning.tolerance, partialFactor: tuning.partialFactor,
        };
      const r = await gradeTuning(payload);
      setResult(r);
      // 채점기 쪽 실패(모범답안을 재지 못함)는 학생 탓이 아니다 — 횟수에 넣지 않고, 같은 답으로 다시 낼 수 있게 둔다
      if (r && r.notCounted) return;
      setGradedKey(sameAnswerKey(answer));
      // 그 사이 표가 지워졌을 수도 있다 — 채점이 「표 없음」 이면 사전 확인 상태도 맞춘다
      if (r && r.outcome === 'no_table') setReadiness({ ready: false, reason: 'no_table', table: r.detail?.table });
      // 저장은 부모가 한다 — 여기서 이력을 건드리면 002·001 과 경로가 갈린다
      if (onGraded) await onGraded({ ...r, submitted: answer });
    } catch (err) {
      // 백엔드 문구를 그대로 쓰지 않는다 — errorType 이 있으면 번역된다(CLAUDE.md 규칙)
      setError(formatApiError(err, t));
    } finally {
      setGrading(false); setGradeId(null);
    }
  }, [answer, isIndex, onGraded, quiz, t, tuning, scope, withIndex, sqlPart, ddlPart, dbms]);

  // 초기화 — 지난 제출·횟수를 지우고 처음부터. 되돌릴 수 없으니 묻는다(브라우저 confirm 이 아니라 공통 대화상자)
  const handleReset = useCallback(async () => {
    if (!onReset) return;
    const ok = await confirm({
      title: t('challenge:tuning.resetTitle'),
      message: t('challenge:tuning.resetConfirm'),
      confirmText: t('challenge:tuning.reset'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await onReset();
      setAnswer(''); setResult(null); setReview(null); setError(''); setGradedKey(null);
    } catch (err) {
      setError(formatApiError(err, t));
    }
  }, [confirm, onReset, t, setAnswer]);

  const handleCancel = useCallback(async () => {
    if (gradeId) await cancelTuning(gradeId);
  }, [gradeId]);

  const handleReview = useCallback(async () => {
    if (!result) return;
    setReviewing(true); setError('');
    try {
      const r = await requestReview({
        quizText: quiz?.QUIZ_DC || '',
        answerSql: isIndex ? (tuning.probeSql || '') : (quiz?.CRANS_SQL_CN || ''),
        submittedSql: answer,
        machine: { score: result.score, reason: result.reason, detail: result.detail },
        metrics: result.metrics, plans: result.plans,
      });
      setReview(r);
      if (onGraded) await onGraded({ ...result, review: r, submitted: answer });
    } catch (err) {
      setError(formatApiError(err, t));
    } finally {
      setReviewing(false);
    }
  }, [answer, isIndex, onGraded, quiz, result, t, tuning]);

  // 채점이 잰 계획을 왼쪽 실행계획 탭으로 — 모범답안과 내 제출을 나란히(2026-10-05).
  // 004 는 두 쪽 모두 측정 조회(probeSql)이고, 각각 모범 인덱스·내 인덱스를 만든 상태의 계획이다
  const hasPlans = !!(result && result.plans && (result.plans.answer || result.plans.submitted));
  const handleShowPlans = useCallback(() => {
    if (!onShowPlans || !result || !result.plans) return;
    const getConn = tuningGradingTarget().connectionId || null;
    onShowPlans({
      nonce: Date.now(),
      title: t('challenge:tuning.planTabName', { no: quiz?.QUIZ_NO }),
      connectionId: getConn,
      answer: {
        label: t('challenge:tuning.answerBuffers'),
        sql: isIndex ? (tuning.probeSql || '') : (quiz?.CRANS_SQL_CN || ''),
        planText: result.plans.answer || '',
        metrics: { buffers: result.metrics?.answer?.buffers ?? null },
      },
      submitted: {
        label: t('challenge:tuning.mineBuffers'),
        sql: isIndex ? (tuning.probeSql || '') : (answer || ''),
        planText: result.plans.submitted || '',
        metrics: { buffers: result.metrics?.submitted?.buffers ?? null },
      },
    });
  }, [onShowPlans, result, t, quiz, isIndex, tuning, answer]);

  // 문제 쿼리의 실행계획 — 쿼리문 옆 「실행계획 보기」(2026-10-05). 풀기 전에 지금 어떻게 도는지를 왼쪽 실행계획 탭에.
  // 채점과 같은 DB·같은 측정(서버 tuning-plan)이라 「개선 전」 수치와 같은 잣대다. 003 = 개선 대상 SQL, 004 = 측정 조회(인덱스 없이)
  const quizSql = isIndex ? (tuning.probeSql || '') : (tuning.slowSql || '');
  const [quizPlanLoading, setQuizPlanLoading] = useState(false);
  const handleQuizPlan = useCallback(async (fallbackSql) => {
    const sql = quizSql || fallbackSql;
    if (!onShowPlans || !sql) return;
    const target = tuningGradingTarget();
    if (target.error) { setError(t('challenge:tuning.needConnection')); return; }
    setQuizPlanLoading(true); setError('');
    try {
      const r = await fetchTuningPlan({ connectionId: target.connectionId, sql, dbms });
      onShowPlans({
        nonce: Date.now(),
        title: t('challenge:tuning.quizPlanTab', { no: quiz?.QUIZ_NO }),
        connectionId: target.connectionId || null,
        answer: {
          label: isIndex ? t('challenge:tuning.quizPlanNoIndex') : t('challenge:tuning.quizPlanBefore'),
          sql, planText: r.planText || '', metrics: { buffers: r.metrics?.buffers ?? null },
        },
      });
    } catch (err) {
      setError(formatApiError(err, t));
    } finally {
      setQuizPlanLoading(false);
    }
  }, [quizSql, onShowPlans, t, quiz, isIndex, dbms]);

  // 지문의 SQL 블록 오른쪽 위에 버튼을 단다 — 「쿼리문 옆」. 블록 글자는 구조화된 값(slowSql/probeSql)이 없을 때만 쓴다
  const mdWithPlan = useMemo(() => {
    if (!onShowPlans) return mdComponents;
    return {
      ...mdComponents,
      pre: (p) => {
        const codeText = (() => {
          const kid = Array.isArray(p.children) ? p.children[0] : p.children;
          const inner = kid && kid.props ? kid.props.children : '';
          return Array.isArray(inner) ? inner.join('') : String(inner || '');
        })();
        return (
          <div style={{ position: 'relative' }}>
            {mdComponents.pre(p)}
            <button
              type="button"
              onClick={() => handleQuizPlan(codeText)}
              disabled={quizPlanLoading || blocked}
              title={t('challenge:tuning.quizPlanHint')}
              style={{
                position: 'absolute', top: 4, right: 4, fontSize: 10, padding: '2px 8px', borderRadius: 3,
                display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
                background: 'var(--color-bg-secondary)', color: 'var(--color-text)', border: '1px solid var(--color-border)',
                cursor: quizPlanLoading ? 'wait' : (blocked ? 'not-allowed' : 'pointer'),
                transition: 'background 150ms ease, border-color 150ms ease',
              }}
            >
              <FiActivity size={11} />
              {quizPlanLoading ? t('challenge:tuning.quizPlanLoading') : t('challenge:tuning.showPlans')}
            </button>
          </div>
        );
      },
    };
  }, [onShowPlans, handleQuizPlan, quizPlanLoading, blocked, t]);

  // 최종 점수 — 재심이 있으면 그쪽이지만, **바뀌었다는 사실을 함께 보여 준다**
  const finalScore = review && !review.invalid ? review.score : result?.score;
  const sc = scoreStyle(finalScore ?? 0);

  return (
    <div style={{ padding: 8, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      <TuningRubricDialog open={rubricOpen} onClose={() => setRubricOpen(false)} scope={scope} thresholds={tuning.thresholds} />
      {/* 스크롤은 **여기**가 맡는다. 뿌리에 걸면 마지막 항목(제출 버튼)이 아래 여백과 함께
          잘린다 — 실제로 버튼 아래쪽이 잘려 보였다(지적 2026-09-19). */}
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* 문제 */}
      <div style={{ flexShrink: 0, minWidth: 0 }}>
        <div style={{ fontWeight: 'bold', color: 'var(--color-text)', fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiFileText size={14} />
          {t('challenge:tuning.quizLabel', { no: quiz?.QUIZ_NO })}
          <span style={{ fontSize: 11, fontWeight: 'normal', color: 'var(--color-text-secondary)' }}>
            {isIndex ? t('challenge:tuning.kindIndex') : t('challenge:tuning.kindSql')}
          </span>
          {/* DBMS — 같은 데이터라도 옵티마이저가 달라 답이 갈린다 */}
          <span style={{ fontSize: 10, fontWeight: 'normal', padding: '1px 6px', borderRadius: 3, border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
            {t(`challenge:dbms.${dbms}`)}
          </span>
          {/* 평가 기준 — 무엇으로 몇 점인지 풀기 전에 볼 수 있어야 한다(2026-10-05) */}
          <button
            type="button"
            onClick={() => setRubricOpen(true)}
            title={t('challenge:rubric.buttonHint')}
            style={{
              marginLeft: 'auto', fontSize: 10, fontWeight: 'normal', padding: '2px 8px', borderRadius: 3,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'var(--color-bg-secondary)', color: 'var(--color-text)', border: '1px solid var(--color-border)',
              cursor: 'pointer', transition: 'background 150ms ease, border-color 150ms ease',
            }}
          >
            <FiAward size={11} />
            {t('challenge:rubric.button')}
          </button>
        </div>
        {/* `box` 의 `pre-wrap` 을 **끄는 것이 중요하다.** 마크다운은 요소로 렌더되는데
            pre-wrap 이 걸리면 요소 사이의 줄바꿈까지 **빈 줄로 그려져** 지문이 띄엄띄엄
            벌어진다(지적 2026-09-19). 줄바꿈은 마크다운이 알아서 한다. */}
        <div style={{ ...box, whiteSpace: 'normal', fontFamily: 'inherit', fontSize: 12, maxHeight: 'calc(34vh / var(--app-zoom, 1))' }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdWithPlan}>{quiz?.QUIZ_DC || ''}</ReactMarkdown>
        </div>
      </div>

      {quiz?.QUIZ_HINT && (
        <div style={{ flexShrink: 0, padding: 8, background: 'var(--color-quiz-warn-bg)', border: '1px solid var(--color-quiz-warn-border)', borderRadius: 4, fontSize: 11, color: 'var(--color-quiz-warn-text)' }}>
          <FiInfo size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{quiz.QUIZ_HINT}
        </div>
      )}

      {/* 지난 제출 — **제출하고 나면 무엇을 냈는지 보여야 한다.** 점수만 남고 답이 사라지면
          다음에 와서 "내가 뭘 냈더라" 를 알 수 없다(지적 2026-09-20). */}
      {last && (
        <div style={{ flexShrink: 0, padding: 8, borderRadius: 4, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 'bold', color: 'var(--color-text)' }}>
              {t('challenge:tuning.lastSubmission')}
            </span>
            {last.score != null && (
              <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                {t('challenge:tuning.score', { score: last.score })}
              </span>
            )}
            <button
              onClick={() => setAnswer(last.sql)}
              style={{
                marginLeft: 'auto', fontSize: 10, padding: '2px 8px', borderRadius: 3,
                background: 'var(--color-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', cursor: 'pointer',
              }}
            >
              {t('challenge:tuning.loadIntoInput')}
            </button>
            {onReset && (
              <button
                onClick={handleReset}
                disabled={grading || isSaving}
                title={t('challenge:tuning.resetHint')}
                style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 3,
                  background: 'var(--color-bg)', color: 'var(--color-quiz-fail-text)',
                  border: '1px solid var(--color-border)', cursor: grading || isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                {t('challenge:tuning.reset')}
              </button>
            )}
          </div>
          <pre style={{
            margin: 0, padding: 6, maxHeight: 96, overflow: 'auto',
            background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 3,
            fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
          }}>{last.sql}</pre>
        </div>
      )}

      {/* 사전 확인 — 이대로는 제출할 수 없는 이유와 고치는 법. 고친 뒤 「다시 확인」 */}
      {blocked && (
        <div
          role="alert"
          style={{
            flexShrink: 0, padding: 8, borderRadius: 4, fontSize: 11, lineHeight: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 6,
            background: 'var(--color-quiz-warn-bg)', border: '1px solid var(--color-quiz-warn-border)', color: 'var(--color-quiz-warn-text)',
            animation: REDUCED_MOTION ? 'none' : 'txn-chip-fade 160ms ease-out',
          }}
        >
          <FiAlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ flex: 1 }}>
            {readiness.reason === 'no_table'
              ? t(readiness.serverDb ? 'challenge:tuning.notReady.noTableServer' : 'challenge:tuning.notReady.noTable', { table: readiness.table || targetTable })
              : readiness.reason === 'db_unavailable'
                ? t('challenge:tuning.notReady.dbUnavailable')
                : t('challenge:tuning.notReady.noConnection')}
          </span>
          <button
            onClick={runReadiness}
            disabled={checking}
            style={{
              flexShrink: 0, fontSize: 10, padding: '2px 8px', borderRadius: 3,
              background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', cursor: checking ? 'wait' : 'pointer',
            }}
          >
            <FiRefreshCw size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
            {checking ? t('challenge:tuning.notReady.checking') : t('challenge:tuning.notReady.recheck')}
          </button>
        </div>
      )}

      {/* 답안 */}
      <div style={{ flex: 1, minHeight: withIndex ? 220 : 120, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
          {withIndex ? t('challenge:tuning.inputSqlOptional') : t('challenge:tuning.inputSql')}
          {' · '}
          {t('challenge:tuning.remaining', { n: remaining, max: maxSubmissions })}
        </div>
        <textarea
          value={sqlPart}
          onChange={(e) => setSqlPart(e.target.value)}
          placeholder={withIndex ? t('challenge:tuning.sqlOptionalPlaceholder') : 'SELECT ...'}
          spellCheck={false}
          aria-label={withIndex ? t('challenge:tuning.inputSqlOptional') : t('challenge:tuning.inputSql')}
          style={{ ...answerBox, minHeight: withIndex ? 80 : 100 }}
        />
        {/* 인덱스 변경까지인 과제만 — 1차(쿼리만)와 2차(인덱스 포함)를 따로 잰다 */}
        {withIndex && (
          <>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: '6px 0 4px' }}>
              {t('challenge:tuning.inputIndexOptional')}
            </div>
            <textarea
              value={ddlPart}
              onChange={(e) => setDdlPart(e.target.value)}
              placeholder="CREATE INDEX ix_name ON TABLE (COL1, COL2);"
              spellCheck={false}
              aria-label={t('challenge:tuning.inputIndexOptional')}
              style={{ ...answerBox, minHeight: 60 }}
            />
          </>
        )}
      </div>

      <div ref={resultRef} />
      {error && (
        <div style={{ flexShrink: 0, padding: 8, borderRadius: 4, fontSize: 11, background: 'var(--color-quiz-fail-bg)', border: '1px solid var(--color-quiz-fail-border)', color: 'var(--color-quiz-fail-text)' }}>
          <FiAlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{error}
        </div>
      )}

      {/* 채점 결과 — **자동 채점과 AI 재심을 나눠** 보인다(2026-10-05). 예전엔 재심 뒤 자동 채점의 이유가 AI 의견으로
          통째로 바뀌고, 점수가 같으면 재심이 돌았다는 표시조차 없어 무엇이 달라졌는지 알 수 없었다 */}
      {result && (
        <div style={{ flexShrink: 0, padding: 8, borderRadius: 4, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text }}>
          <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
            {t('challenge:tuning.score', { score: finalScore })}
            {review && !review.invalid && (
              <span style={{ fontSize: 11, fontWeight: 'normal', marginLeft: 6 }}>
                {review.changed
                  ? t('challenge:tuning.reviewChanged', { from: result.score, to: review.score })
                  : t('challenge:tuning.reviewSame', { score: review.score })}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.5 }}>
            <strong>{t('challenge:tuning.machineLabel', { score: result.score })}</strong> {reasonOf(t, result)}
          </div>
          {review && !review.invalid && (
            <div style={{ fontSize: 11, lineHeight: 1.5, marginTop: 6, paddingTop: 6, borderTop: '1px dashed currentColor' }}>
              <div>
                <FiCpu size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                <strong>{t('challenge:tuning.reviewLabel')}</strong>{' '}
                {review.changed
                  ? t('challenge:tuning.reviewChanged', { from: result.score, to: review.score })
                  : t('challenge:tuning.reviewSame', { score: review.score })}
              </div>
              <div style={{ marginTop: 2 }}>{review.reason}</div>
              {review.beyond && (
                <div style={{ marginTop: 4, opacity: 0.9 }}>
                  <strong>{t('challenge:tuning.beyondLabel')}</strong> {review.beyond}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 수치 세 개 — 점수만 주면 왜 그런지 알 수 없다 */}
      {result && (
        <div style={{ flexShrink: 0, display: 'flex', gap: 6, fontSize: 11 }}>
          {[
            // 개선 전은 이번 채점이 잰 값(없으면 출제 때 값) — 0점 기준(규칙 6)이 이것이다
            { k: 'before', label: t('challenge:tuning.beforeBuffers'), v: result.metrics?.baseline?.buffers ?? tuning.baselineBuffers },
            { k: 'answer', label: t('challenge:tuning.answerBuffers'), v: result.metrics?.answer?.buffers },
            ...(result.metrics?.phase2
              ? [
                { k: 'p1', label: t('challenge:tuning.phase1Buffers'), v: result.metrics?.phase1?.buffers },
                { k: 'p2', label: t('challenge:tuning.phase2Buffers'), v: result.metrics?.phase2?.buffers },
              ]
              : [{ k: 'mine', label: t('challenge:tuning.mineBuffers'), v: result.metrics?.submitted?.buffers }]),
          ].map(x => (
            <div key={x.k} style={{ flex: 1, padding: 6, borderRadius: 4, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', textAlign: 'center' }}>
              <div style={{ color: 'var(--color-text-secondary)' }}>{x.label}</div>
              <div style={{ fontWeight: 'bold', color: 'var(--color-text)', fontSize: 13 }}>
                {x.v == null ? '—' : Number(x.v).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 실행계획 — 왼쪽 실행계획 탭에 모범답안과 내 제출을 나란히 연다 */}
      {hasPlans && onShowPlans && (
        <button
          onClick={handleShowPlans}
          title={t('challenge:tuning.showPlansHint')}
          style={{
            flexShrink: 0, padding: '6px 12px', borderRadius: 4, fontSize: 11,
            background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
            border: '1px solid var(--color-border)', cursor: 'pointer',
          }}
        >
          <FiActivity size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {t('challenge:tuning.showPlans')}
        </button>
      )}

      {/* 재심 — AI 제외 빌드에서는 버튼 자체가 없다 */}
      {result && reviewAvailable() && !review && (
        <button
          onClick={handleReview}
          disabled={reviewing}
          style={{
            flexShrink: 0, padding: '6px 12px', borderRadius: 4, fontSize: 11,
            background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
            border: '1px solid var(--color-border)', cursor: reviewing ? 'wait' : 'pointer',
          }}
        >
          <FiCpu size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {reviewing ? t('challenge:tuning.reviewing') : t('challenge:tuning.requestReview')}
        </button>
      )}
      {review && review.invalid && (
        <div style={{ flexShrink: 0, fontSize: 11, color: 'var(--color-text-secondary)' }}>
          {t('challenge:tuning.reviewInvalid')}
        </div>
      )}

      {result?.notCounted && (
        <div style={{ flexShrink: 0, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          · {t('challenge:tuning.notCounted')}
        </div>
      )}
      {/* 서버가 남긴 주의 — 못 지운 임시 인덱스 같은 것 */}
      {result?.notes?.length > 0 && (
        <div style={{ flexShrink: 0, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          {result.notes.map((note, i) => <div key={i}>· {noteText(t, note)}</div>)}
        </div>
      )}
      </div>
      {/* 버튼 — 그라데이션을 쓰지 않는다. 주 동작만 채움색 하나.
          **스크롤 밖 바닥에 고정한다** — 지문·지난 제출·경고가 길면 스크롤 안의 제출 버튼이 아래로 밀려 안 보였다(지적 2026-10-04). */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, justifyContent: 'flex-end', alignItems: 'center', paddingTop: 8, marginTop: 4, borderTop: '1px solid var(--color-border)' }}>
        {unchanged && !grading && (
          <span style={{ flex: 1, fontSize: 11, color: 'var(--color-text-secondary)' }}>{t('challenge:tuning.unchanged')}</span>
        )}
        <button
          onClick={handleGrade}
          disabled={!canGrade || isSaving}
          title={unchanged ? t('challenge:tuning.unchanged') : undefined}
          style={{
            minWidth: 150, padding: '8px 16px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: canGrade ? 'var(--color-accent)' : 'var(--color-text-muted)',
            color: 'var(--color-bg)', border: 'none',
            cursor: canGrade && !isSaving ? 'pointer' : 'not-allowed',
            transition: 'background 150ms ease',
          }}
        >
          <FiPlay size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {grading ? t('challenge:tuning.grading') : t('challenge:tuning.grade')}
        </button>
        {grading && (
          <button
            onClick={handleCancel}
            style={{
              padding: '8px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
              background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', cursor: 'pointer',
            }}
          >
            <FiSquare size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t('challenge:tuning.stop')}
          </button>
        )}
      </div>
    </div>
  );
};

export default TuningQuiz;
